import { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
    X,
    Loader2,
    CheckCircle2,
    AlertTriangle,
    Download,
    Calendar,
} from "lucide-react";
import { useFocusTrap } from "@/components/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Module-level option arrays — defined once, not re-created per render.
const COMPLIANCE_STACK_OPTIONS = Object.freeze([
    "Spreadsheets / SharePoint",
    "Single legacy compliance vendor",
    "Multiple vendors\u00a0(workforce + plant + subby)",
    "In-house build",
    "Reviewing options",
]);

const WORKFORCE_SIZE_OPTIONS = Object.freeze([
    "Up to 250",
    "250\u20131,000",
    "1,000\u20135,000",
    "5,000\u201320,000",
    "20,000+",
]);

const INITIAL_BOOK_FORM = Object.freeze({
    name: "",
    work_email: "",
    company: "",
    role: "",
    project_name: "",
    country: "Australia",
    current_compliance_stack: "",
    workforce_size: "",
});

const INITIAL_CAPABILITY_FORM = Object.freeze({
    name: "",
    work_email: "",
    company: "",
});

const INITIAL_STATE = Object.freeze({
    loading: false,
    success: false,
    error: null,
});

// =====================================================================
// Shared modal shell
// =====================================================================
function ModalShell({ open, onClose, labelledBy, children, testId }) {
    const ref = useRef(null);
    useFocusTrap(ref, open);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = prev;
        };
    }, [open, onClose]);

    if (!open) return null;
    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={labelledBy}
            data-testid={testId}
            className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/70 backdrop-blur-sm overflow-hidden"
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div
                ref={ref}
                className="modal-card relative w-full sm:max-w-2xl bg-white text-[#0A0A0A] rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto"
                onMouseDown={(e) => e.stopPropagation()}
            >
                {children}
            </div>
        </div>
    );
}

// =====================================================================
// Book a demo modal — 8 fields, primary conversion
// =====================================================================
export function BookDemoModal({ open, onClose }) {
    const [form, setForm] = useState({ ...INITIAL_BOOK_FORM });
    const [state, setState] = useState({ ...INITIAL_STATE });

    useEffect(() => {
        if (open) {
            setState({ ...INITIAL_STATE });
        }
    }, [open]);

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setState({ loading: true, success: false, error: null });
        try {
            const res = await axios.post(`${API}/book-demo`, form);
            if (res.status === 200 || res.status === 201) {
                setState({ loading: false, success: true, error: null });
            } else {
                setState({
                    loading: false,
                    success: false,
                    error: "Unexpected response. Please try again.",
                });
            }
        } catch (err) {
            const detail =
                err?.response?.data?.detail ||
                err?.message ||
                "Submission failed. Please try again.";
            setState({
                loading: false,
                success: false,
                error: typeof detail === "string" ? detail : "Submission failed.",
            });
        }
    };

    return (
        <ModalShell
            open={open}
            onClose={onClose}
            labelledBy="book-demo-title"
            testId="book-demo-modal"
        >
            <div className="flex items-start justify-between p-6 sm:p-7 border-b border-zinc-200">
                <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#1FA8FF] mb-1">
                        Enterprise demo
                    </div>
                    <h3
                        id="book-demo-title"
                        className="font-display text-3xl sm:text-4xl text-[#0A0A0A] leading-none"
                    >
                        Book a working session.
                    </h3>
                    <p className="mt-2 text-sm text-zinc-600 max-w-md">
                        We will walk a real project scenario through the
                        platform with your HSE and digital engineering leads.
                    </p>
                </div>
                <button
                    aria-label="Close"
                    onClick={onClose}
                    className="p-2 rounded-md hover:bg-zinc-100 -mt-1 -mr-2"
                    data-testid="close-book-demo"
                >
                    <X className="w-5 h-5" />
                </button>
            </div>

            {state.success ? (
                <SuccessPanel
                    title="Booking received."
                    body="The Edara Software enterprise team will be in touch within one business day to schedule your working session. The founder typically takes the first call."
                    onClose={onClose}
                    icon={<Calendar className="w-6 h-6" />}
                />
            ) : (
                <form
                    onSubmit={submit}
                    className="p-6 sm:p-7 grid grid-cols-1 sm:grid-cols-2 gap-4"
                    data-testid="book-demo-form"
                    noValidate
                >
                    <Field
                        label="Full name"
                        required
                        value={form.name}
                        onChange={update("name")}
                        testId="field-name"
                    />
                    <Field
                        label="Work email"
                        type="email"
                        required
                        value={form.work_email}
                        onChange={update("work_email")}
                        testId="field-email"
                    />
                    <Field
                        label="Company"
                        required
                        value={form.company}
                        onChange={update("company")}
                        testId="field-company"
                    />
                    <Field
                        label="Role"
                        placeholder="e.g. Group HSE Director"
                        required
                        value={form.role}
                        onChange={update("role")}
                        testId="field-role"
                    />
                    <Field
                        label="Project name"
                        required
                        value={form.project_name}
                        onChange={update("project_name")}
                        testId="field-project"
                    />
                    <Field
                        label="Country"
                        required
                        value={form.country}
                        onChange={update("country")}
                        testId="field-country"
                    />
                    <SelectField
                        label="Current compliance stack"
                        required
                        value={form.current_compliance_stack}
                        onChange={update("current_compliance_stack")}
                        testId="field-stack"
                        options={COMPLIANCE_STACK_OPTIONS}
                    />
                    <SelectField
                        label="Rough workforce size"
                        required
                        value={form.workforce_size}
                        onChange={update("workforce_size")}
                        testId="field-size"
                        options={WORKFORCE_SIZE_OPTIONS}
                    />

                    {state.error && (
                        <div
                            className="sm:col-span-2 flex items-start gap-2 text-sm text-[#B45309] bg-[#FEF3C7] border border-[#F59E0B]/40 rounded-lg p-3"
                            role="alert"
                        >
                            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                            <span>{state.error}</span>
                        </div>
                    )}

                    <div className="sm:col-span-2 flex flex-col-reverse sm:flex-row gap-3 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-5 py-3 rounded-lg border border-zinc-300 text-[#0A0A0A] hover:bg-zinc-100"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={state.loading}
                            className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#1FA8FF] text-white font-semibold hover:bg-[#0C84D4] disabled:opacity-60 transition-colors"
                            data-testid="submit-book-demo"
                        >
                            {state.loading ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Submitting…
                                </>
                            ) : (
                                <>Request demo</>
                            )}
                        </button>
                    </div>

                    <p className="sm:col-span-2 text-[11px] text-zinc-500 leading-relaxed">
                        By submitting, you agree to be contacted by Edara
                        Software about the working session. Your details are
                        used only for this purpose.
                    </p>
                </form>
            )}
        </ModalShell>
    );
}

// =====================================================================
// Capability statement modal — 3 fields, then PDF download
// =====================================================================
export function CapabilityModal({ open, onClose }) {
    const [form, setForm] = useState({ ...INITIAL_CAPABILITY_FORM });
    const [state, setState] = useState({ ...INITIAL_STATE });

    useEffect(() => {
        if (open) {
            setState({ ...INITIAL_STATE });
        }
    }, [open]);

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setState({ loading: true, success: false, error: null });
        try {
            const res = await axios.post(`${API}/download-capability`, form, {
                responseType: "blob",
            });
            const blob = new Blob([res.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "Edara-Software-Capability-Statement.pdf";
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            setState({ loading: false, success: true, error: null });
        } catch (err) {
            const detail =
                err?.response?.data?.detail ||
                err?.message ||
                "Download failed. Please try again.";
            setState({
                loading: false,
                success: false,
                error: typeof detail === "string" ? detail : "Download failed.",
            });
        }
    };

    return (
        <ModalShell
            open={open}
            onClose={onClose}
            labelledBy="capability-title"
            testId="capability-modal"
        >
            <div className="flex items-start justify-between p-6 sm:p-7 border-b border-zinc-200">
                <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#1FA8FF] mb-1">
                        Capability statement
                    </div>
                    <h3
                        id="capability-title"
                        className="font-display text-3xl sm:text-4xl text-[#0A0A0A] leading-none"
                    >
                        Download the capability statement.
                    </h3>
                    <p className="mt-2 text-sm text-zinc-600 max-w-md">
                        Platform overview, security posture, integration
                        framework, and selected anonymised case studies.
                    </p>
                </div>
                <button
                    aria-label="Close"
                    onClick={onClose}
                    className="p-2 rounded-md hover:bg-zinc-100 -mt-1 -mr-2"
                    data-testid="close-capability"
                >
                    <X className="w-5 h-5" />
                </button>
            </div>

            {state.success ? (
                <SuccessPanel
                    title="Download started."
                    body="Your capability statement should appear in your browser downloads. If it does not, check the address bar for a blocked download prompt."
                    onClose={onClose}
                    icon={<Download className="w-6 h-6" />}
                />
            ) : (
                <form
                    onSubmit={submit}
                    className="p-6 sm:p-7 grid grid-cols-1 sm:grid-cols-2 gap-4"
                    data-testid="capability-form"
                    noValidate
                >
                    <Field
                        label="Full name"
                        required
                        value={form.name}
                        onChange={update("name")}
                        testId="cap-field-name"
                    />
                    <Field
                        label="Work email"
                        type="email"
                        required
                        value={form.work_email}
                        onChange={update("work_email")}
                        testId="cap-field-email"
                    />
                    <Field
                        label="Company"
                        required
                        wide
                        value={form.company}
                        onChange={update("company")}
                        testId="cap-field-company"
                    />

                    {state.error && (
                        <div
                            className="sm:col-span-2 flex items-start gap-2 text-sm text-[#B45309] bg-[#FEF3C7] border border-[#F59E0B]/40 rounded-lg p-3"
                            role="alert"
                        >
                            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                            <span>{state.error}</span>
                        </div>
                    )}

                    <div className="sm:col-span-2 flex flex-col-reverse sm:flex-row gap-3 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-5 py-3 rounded-lg border border-zinc-300 text-[#0A0A0A] hover:bg-zinc-100"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={state.loading}
                            className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#0A0A0A] text-white font-semibold hover:bg-[#1F2937] disabled:opacity-60 transition-colors"
                            data-testid="submit-capability"
                        >
                            {state.loading ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Preparing PDF…
                                </>
                            ) : (
                                <>
                                    <Download className="w-4 h-4" />
                                    Download PDF
                                </>
                            )}
                        </button>
                    </div>

                    <p className="sm:col-span-2 text-[11px] text-zinc-500 leading-relaxed">
                        Your details are used solely to send you the document
                        and follow up if relevant. No marketing list.
                    </p>
                </form>
            )}
        </ModalShell>
    );
}

// =====================================================================
// Field primitives
// =====================================================================
function Field({
    label,
    type = "text",
    required,
    value,
    onChange,
    placeholder,
    wide,
    testId,
}) {
    return (
        <label
            className={`flex flex-col gap-1.5 ${wide ? "sm:col-span-2" : ""}`}
        >
            <span className="text-[11px] uppercase tracking-widest text-zinc-600">
                {label}
                {required && <span className="text-[#1FA8FF]"> *</span>}
            </span>
            <input
                type={type}
                required={required}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                data-testid={testId}
                className="px-3.5 py-3 rounded-lg border border-zinc-300 bg-white text-[#0A0A0A] placeholder-zinc-400 focus:border-[#1FA8FF] focus:ring-2 focus:ring-[#1FA8FF]/30 outline-none transition-colors"
            />
        </label>
    );
}

function SelectField({ label, required, value, onChange, options, testId }) {
    return (
        <label className="flex flex-col gap-1.5">
            <span className="text-[11px] uppercase tracking-widest text-zinc-600">
                {label}
                {required && <span className="text-[#1FA8FF]"> *</span>}
            </span>
            <select
                required={required}
                value={value}
                onChange={onChange}
                data-testid={testId}
                className="px-3.5 py-3 rounded-lg border border-zinc-300 bg-white text-[#0A0A0A] focus:border-[#1FA8FF] focus:ring-2 focus:ring-[#1FA8FF]/30 outline-none transition-colors"
            >
                <option value="" disabled>
                    Select…
                </option>
                {options.map((o) => (
                    <option key={o} value={o}>
                        {o}
                    </option>
                ))}
            </select>
        </label>
    );
}

function SuccessPanel({ title, body, onClose, icon }) {
    return (
        <div
            className="p-8 sm:p-10 text-center"
            data-testid="modal-success"
        >
            <div className="w-14 h-14 mx-auto rounded-full bg-[#10B981]/15 text-[#10B981] flex items-center justify-center mb-4">
                {icon || <CheckCircle2 className="w-7 h-7" />}
            </div>
            <h4 className="font-display text-3xl text-[#0A0A0A] leading-none">
                {title}
            </h4>
            <p className="mt-3 text-zinc-600 max-w-md mx-auto">{body}</p>
            <button
                onClick={onClose}
                className="mt-6 px-5 py-3 rounded-lg bg-[#0A0A0A] text-white font-semibold hover:bg-[#1F2937] transition-colors"
                data-testid="close-success"
            >
                Close
            </button>
        </div>
    );
}
